/**
 * S2.2: Cegid API Authentication Module
 * Handles token management for all 4 Cegid APIs (CON, ERP, FAC, NOM)
 * with automatic refresh and in-memory caching.
 * 
 * SECURITY: All requests use HTTPS. CON API officially uses HTTP but we force HTTPS.
 */

import type { CegidApiType, CegidCachedToken, CegidAuthConfig } from './types';

// In-memory token cache (keyed by apiType + companyCode)
const tokenCache = new Map<string, CegidCachedToken>();

// API base URLs
const API_URLS: Record<CegidApiType, string> = {
  erp: 'https://apierp.diezsoftware.com',
  con: 'https://apicon.diezsoftware.com', // SECURITY: Force HTTPS even though docs say HTTP
  fac: 'https://apirec.diezsoftware.com',
  nom: 'https://apinom.diezsoftware.com',
};

function getCacheKey(apiType: CegidApiType, companyCode?: string): string {
  return companyCode ? `${apiType}:${companyCode}` : apiType;
}

function getEnvCredentials(): {
  clientId: string;
  userName: string;
  password: string;
  clientSecrets: Record<CegidApiType, string>;
} {
  const clientId = process.env.CEGID_CLIENT_ID || 'EXTERNALIZA2-CONSULTORES-SOCIEDAD-LIMITADA';
  const userName = process.env.CEGID_USERNAME || 'R1007';
  const password = process.env.CEGID_PASSWORD || '';

  if (!password) {
    throw new Error('CEGID_PASSWORD environment variable is required');
  }

  return {
    clientId,
    userName,
    password,
    clientSecrets: {
      erp: process.env.CEGID_SECRET_ERP || '',
      con: process.env.CEGID_SECRET_CON || '',
      fac: process.env.CEGID_SECRET_FAC || '',
      nom: process.env.CEGID_SECRET_NOM || '',
    },
  };
}

/**
 * Authenticate with Cegid ERP API (JWT-based)
 * POST https://apierp.diezsoftware.com/api/auth/login
 */
async function authenticateERP(): Promise<CegidCachedToken> {
  const creds = getEnvCredentials();
  const url = `${API_URLS.erp}/api/auth/login`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userName: creds.userName,
      password: creds.password,
      clientId: creds.clientId,
      clientSecret: creds.clientSecrets.erp,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Cegid ERP auth failed (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  const token = data.token || data.access_token;

  if (!token) {
    throw new Error('Cegid ERP auth: No token in response');
  }

  return {
    accessToken: token,
    expiresAt: Date.now() + 3600 * 1000, // Assume 1 hour if not specified
    apiType: 'erp',
  };
}

/**
 * Authenticate with Cegid CON API (OAuth2 password grant, x-www-form-urlencoded)
 * POST https://apicon.diezsoftware.com/token
 * Requires cod_empresa parameter
 */
async function authenticateCON(companyCode: string): Promise<CegidCachedToken> {
  const creds = getEnvCredentials();
  const url = `${API_URLS.con}/token`;

  const params = new URLSearchParams({
    grant_type: 'password',
    client_id: creds.clientId,
    client_secret: creds.clientSecrets.con,
    username: creds.userName,
    password: creds.password,
    cod_empresa: companyCode,
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Cegid CON auth failed (${response.status}): ${errorText}`);
  }

  const data = await response.json();

  return {
    accessToken: data.access_token,
    expiresAt: Date.now() + (data.expires_in || 3600) * 1000,
    apiType: 'con',
    companyCode,
  };
}

/**
 * Authenticate with Cegid FAC API (JWT-based)
 * POST https://apirec.diezsoftware.com/api/Auth/login
 * Requires empresa parameter (company code)
 */
async function authenticateFAC(companyCode: string): Promise<CegidCachedToken> {
  const creds = getEnvCredentials();
  const url = `${API_URLS.fac}/api/Auth/login`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userName: creds.userName,
      password: creds.password,
      clientId: creds.clientId,
      clientSecret: creds.clientSecrets.fac,
      empresa: companyCode,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Cegid FAC auth failed (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  
  // FAC API returns auth_token, not token or access_token
  const token = data.auth_token || data.token || data.access_token;

  return {
    accessToken: token,
    expiresAt: Date.now() + 3600 * 1000,
    apiType: 'fac',
  };
}

/**
 * Authenticate with Cegid NOM API (JWT-based)
 * POST https://apinom.diezsoftware.com/api/Auth/login
 */
async function authenticateNOM(): Promise<CegidCachedToken> {
  const creds = getEnvCredentials();
  const url = `${API_URLS.nom}/api/Auth/login`;
  const nomClientId = process.env.CEGID_NOM_CLIENT_ID || 'diez-nom-api';

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      userName: creds.userName,
      password: creds.password,
      clientId: nomClientId,
      clientSecret: creds.clientSecrets.nom,
      codigoEmpresa: 0,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Cegid NOM auth failed (${response.status}): ${errorText}`);
  }

  const data = await response.json();
  const token = data.token || data.access_token;

  return {
    accessToken: token,
    expiresAt: Date.now() + 3600 * 1000,
    apiType: 'nom',
  };
}

/**
 * Get a valid token for the specified API, using cache when possible.
 * Automatically refreshes expired tokens.
 */
export async function getToken(
  apiType: CegidApiType,
  companyCode?: string
): Promise<string> {
  const cacheKey = getCacheKey(apiType, companyCode);
  const cached = tokenCache.get(cacheKey);

  // Return cached token if still valid (with 5min buffer)
  if (cached && cached.expiresAt > Date.now() + 5 * 60 * 1000) {
    return cached.accessToken;
  }

  // Authenticate based on API type
  let newToken: CegidCachedToken;

  switch (apiType) {
    case 'erp':
      newToken = await authenticateERP();
      break;
    case 'con':
      if (!companyCode) throw new Error('CON API requires companyCode');
      newToken = await authenticateCON(companyCode);
      break;
    case 'fac':
      if (!companyCode) throw new Error('FAC API requires companyCode');
      newToken = await authenticateFAC(companyCode);
      break;
    case 'nom':
      newToken = await authenticateNOM();
      break;
    default:
      throw new Error(`Unknown API type: ${apiType}`);
  }

  tokenCache.set(cacheKey, newToken);
  return newToken.accessToken;
}

/**
 * Make an authenticated request to a Cegid API.
 * Handles token refresh and retries.
 */
export async function cegidFetch(
  apiType: CegidApiType,
  path: string,
  options: {
    method?: string;
    body?: any;
    params?: Record<string, string>;
    companyCode?: string;
    retries?: number;
  } = {}
): Promise<any> {
  const { method = 'GET', body, params, companyCode, retries = 1 } = options;
  const baseUrl = API_URLS[apiType];

  let url = `${baseUrl}${path}`;
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const token = await getToken(apiType, companyCode);

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };
      if (method !== 'GET') {
        headers['Content-Type'] = 'application/json';
      }

      const fetchOptions: RequestInit = { method, headers };
      if (body && method !== 'GET') {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(url, fetchOptions);

      // If 401, clear cache and retry
      if (response.status === 401 && attempt < retries) {
        const cacheKey = getCacheKey(apiType, companyCode);
        tokenCache.delete(cacheKey);
        continue;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Cegid ${apiType.toUpperCase()} API error (${response.status}): ${errorText}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      }
      return await response.text();
    } catch (error: any) {
      if (attempt === retries) throw error;
      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt)));
    }
  }
}

/**
 * Clear all cached tokens (useful for testing or forced re-auth)
 */
export function clearTokenCache(): void {
  tokenCache.clear();
}

/**
 * Get the base URL for a Cegid API
 */
export function getApiBaseUrl(apiType: CegidApiType): string {
  return API_URLS[apiType];
}
